import React, { useState } from 'react';
import { ClickAwayListener, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Stack from '@mui/material/Stack';
import { Attachment } from 'modules/service-call/types';
import { useRecoilValue } from 'recoil';
import { tenantInfoState } from 'modules/application/atoms';
import { useIntl } from 'react-intl';
import ListItemText from '@mui/material/ListItemText';
import { ServiceCallsApi } from 'modules/service-call/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ServiceCallAttachmentPreview } from './ServiceCallAttachmentPreview';
import { ServiceCallAttachmentDeleteDialog } from './ServiceCallAttachmentDeleteDialog';

interface ServiceCallAttachmentProps {
  attachment: Attachment
  serviceCallId: string
  noteId: number
}

export const ServiceCallAttachment: React.FC<ServiceCallAttachmentProps> = ({ attachment, serviceCallId, noteId }) => {
  const { formatMessage } = useIntl();
  const [anchorElement, setAnchorElement] = useState<Element>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const tenantInfo = useRecoilValue(tenantInfoState);
  const queryClient = useQueryClient();

  const isMenuOpen = Boolean(anchorElement);
  const attachmentSrc = `/${tenantInfo.identifier}/api/servicecalls/${serviceCallId}/notes/${noteId}/attachments/${attachment.id}`;
  const attachmentDownloadUrl = `${attachmentSrc}/download`;

  const invalidateServiceCallQueryMutation = useMutation(() => queryClient.invalidateQueries(['service-call', serviceCallId]));
  const deleteAttachmentMutation = useMutation(() => ServiceCallsApi.deleteAttachment(serviceCallId, noteId, attachment.id));

  const openMenu = (event: React.MouseEvent<Element, MouseEvent>) => {
    setAnchorElement(event.currentTarget);
  };

  const closeMenu = () => setAnchorElement(undefined);

  const handleDeleteAttachmentClicked = () => {
    closeMenu();
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteAttachment = () => {
    deleteAttachmentMutation
      .mutateAsync()
      .then(() => invalidateServiceCallQueryMutation.mutateAsync());
  };

  return (
    <Stack direction='row' justifyContent='space-between' alignItems='center' >
      <ServiceCallAttachmentPreview attachment={attachment} serviceCallId={serviceCallId} noteId={noteId} />
      <Menu open={isMenuOpen} anchorEl={anchorElement}>
        <ClickAwayListener onClickAway={closeMenu}>
          <Stack>
            <MenuItem
              href={attachmentSrc}
              target='_blank'
              component='a'
              onClick={closeMenu}
            >
              <ListItemIcon>
                <OpenInNewIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({ id: 'ServiceCallAttachment.Open', defaultMessage: 'Open' })}
              </ListItemText>
            </MenuItem>
            <MenuItem
              href={attachmentDownloadUrl}
              target='_blank'
              component='a'
              download
              onClick={closeMenu}
            >
              <ListItemIcon>
                <FileDownloadIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({ id: 'ServiceCallAttachment.Download', defaultMessage: 'Download' })}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleDeleteAttachmentClicked}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage({ id: 'ServiceCallAttachment.Delete', defaultMessage: 'Delete' })}
              </ListItemText>
            </MenuItem>
          </Stack>
        </ClickAwayListener>
      </Menu>
      <ServiceCallAttachmentDeleteDialog
        isOpen={isDeleteDialogOpen}
        attachment={attachment}
        noteId={noteId}
        serviceCallId={serviceCallId}
        onClose={handleCloseDialog}
        onDelete={handleDeleteAttachment} />
      <Stack>
        <IconButton onClick={openMenu} disabled={deleteAttachmentMutation.isLoading || invalidateServiceCallQueryMutation.isLoading} >
          <MoreVertIcon />
        </IconButton>
      </Stack>
    </Stack >
  );
};